import { type QueryClient, queryOptions, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import ms from 'ms'
import { useMemo } from 'react'

import useViewContextAwareHttpClient from '@utils/ViewContextAwareHttpClient'
import { ProblemDetailsError } from '@utils/errors.ts'
import { isAnyValueSet, MyUrlSearchParams } from '@utils/index'
import { getNextPageParam, type PaginationInfo } from '@utils/pagination/pagination'
import { useStockLevelMapper } from '@utils/stockLevelMapper.ts'

import I18n from '../../i18n'

import type { components, operations } from '../../schema'

export type ProductDTO = components['schemas']['ProductResponse']
type ProductPlacementDTO = components['schemas']['Placement']
export type StockLevel = ProductPlacementDTO['stockLevel']

export class ProductWrapper implements ProductDTO {
  gtin: string
  name: string
  brandName: string
  imageUrl?: string | undefined
  baseCategory?: string | undefined
  placements: ProductDTO['placements']
  reportedPrice?: ProductDTO['reportedPrice']
  catalogPrice?: ProductDTO['catalogPrice']
  inventory?: NonNullable<ProductDTO['inventory']>
  imageOutline?: ProductDTO['imageOutline']

  private readonly fallbackStockLevel: StockLevel

  constructor(product: ProductDTO, fallbackStockLevel: StockLevel = 'UNKNOWN') {
    this.fallbackStockLevel = fallbackStockLevel
    this.gtin = product.gtin
    this.name = product.name
    this.brandName = product.brandName
    this.imageUrl = product.imageUrl ?? undefined
    this.baseCategory = product.baseCategory ?? undefined
    this.placements = this.mapPlacements(product.placements)
    this.reportedPrice = product.reportedPrice
    this.catalogPrice = product.catalogPrice
    this.inventory = product.inventory ?? undefined
    this.imageOutline = product.imageOutline
  }

  private mapPlacements(placements: ProductDTO['placements']) {
    return placements.map(placement => ({
      shelfId: placement.shelfId,
      stockLevel: this.mapStockLevel(placement),
      facingId: placement.facingId,
      current: placement.current,
    }))
  }

  // not current means not judged: it must not fall back to "in stock" like a plain UNKNOWN
  private mapStockLevel({ stockLevel, current }: ProductDTO['placements'][number]): StockLevel {
    if (current === false) return 'UNKNOWN'
    return stockLevel === 'UNKNOWN' ? this.fallbackStockLevel : stockLevel
  }

  aggregateStockLevel(
    strategy: 'optimistic' | 'pessimistic'
  ): StockLevel {

    if (this.placements.length === 0) {
      return this.fallbackStockLevel
    }

    if (this.placements.every(p => p.current === false)) {
      return 'UNKNOWN'
    }

    switch (strategy) {
      case 'optimistic':
        // If ANY placement has stock, the product is available.
        if (this.placements.some(p => p.stockLevel === 'HAS_STOCK')) {
          return 'HAS_STOCK'
        }
        // If we reach here, none have stock. If ALL are confirmed 'NO_STOCK', then it's out.
        if (this.placements.every(p => p.stockLevel === 'NO_STOCK')) {
          return 'NO_STOCK'
        }
        return this.fallbackStockLevel

      case 'pessimistic':
        // If ANY placement is out of stock, the whole product is considered unavailable.
        if (this.placements.some(p => p.stockLevel === 'NO_STOCK')) {
          return 'NO_STOCK'
        }
        // If we reach here, none are out of stock. If ANY have stock, it's available.
        if (this.placements.some(p => p.stockLevel === 'HAS_STOCK')) {
          return 'HAS_STOCK'
        }
        return this.fallbackStockLevel
    }
  }

  getPriceTag(priceType: 'reported' | 'catalog') {
    const monetary = priceType === 'reported' ? this.reportedPrice : this.catalogPrice

    if (!monetary) return undefined

    switch (monetary.currency) {
      case 'EUR':
        return `${new Intl.NumberFormat(I18n.language).format(monetary.amount)} €`
      default: {
        console.warn('Found unknown currency: ', monetary.currency)
        return monetary.currency // the name itself is the fallback
      }
    }
  }

  hasPositiveInventory() {
    if (this.inventory === undefined) return undefined

    return this.inventory.quantity > 0
  }

  getShelfIds(): string[] {
    return this.placements.map(p => p.shelfId)
  }
}

type ProductSearchParams = NonNullable<operations['searchForProductsWithScanId']['parameters']['query']>
export type ProductSearchResponse = operations['searchForProductsWithScanId']['responses']['200']['content']['application/json']

export type InvalidGtinError = components['schemas']['InvalidGtinProblem']

const productKeys = {
  all: ['products'] as const,
  search: (storeId: string, scanId: string, params: ProductSearchParams) => [...productKeys.all, storeId, scanId, params] as const,
}

interface ProductSearchOptionParams {
  get: (url: string) => Promise<ProductSearchResponse>
  storeId: string
  scanId: string
  search: ProductSearchParams
  productMapper: (product: ProductDTO) => ProductWrapper
}

function getProductSearchOptions({ get, search, storeId, scanId, productMapper }: ProductSearchOptionParams) {
  return queryOptions<SearchResult, InvalidGtinError | ProblemDetailsError>({
    queryKey: productKeys.search(storeId, scanId, search),
    queryFn: async (ctx) => {
      const url = `/products${new MyUrlSearchParams(search).toString()}`
      const searchResult = await get(url)

      const mappedItems = searchResult.items.map(productMapper)

      if (search.term) {
        mappedItems.forEach(product => seedProductByGtinCache(ctx.client, storeId, scanId, product))
      }

      return {
        items: mappedItems,
        pagination: searchResult.pagination,
      }
    },
    enabled: isAnyValueSet(search),
    staleTime: ms('1d'),
  })
}

function useProductSearch(search: ProductSearchParams) {
  const { storeId, viewKey, get } = useViewContextAwareHttpClient()
  const productMapper = useProductMapper()
  return useQuery(getProductSearchOptions({ get, storeId, scanId: viewKey, search, productMapper }))
}

function useProductSearchImp() {
  const client = useQueryClient()
  const { storeId, viewKey, get } = useViewContextAwareHttpClient()
  const productMapper = useProductMapper()

  return (search: ProductSearchParams) => client.query(getProductSearchOptions({ get, storeId, scanId: viewKey, search, productMapper }))
}

export function useProductSearchByGTIN(gtin: string | undefined) {
  return useProductSearch({ gtin })
}

export function useProductsFromShelf(shelfId: string) {
  const { data, isLoading } = useProductSearch({ shelfId, limit: 100 })
  return { products: data?.items ?? [], isLoading }
}

export function useProductSearchByGTINImp() {
  const productSearch = useProductSearchImp()
  return (gtin: string | undefined) => productSearch({ gtin })
}

export function usePlacementsByGtin(gtin: string | undefined) {
  const { data: result } = useProductSearchByGTIN(gtin)

  return useMemo(() => {
    if (!result) return []
    const [firstProduct] = result.items
    if (!firstProduct) return []

    return firstProduct.placements.map(placement => placement.shelfId)
  }, [result])
}

interface SearchResult {
  items: ProductWrapper[]
  pagination: PaginationInfo
}

export function useProductSearchInfinite(filter: Omit<ProductSearchParams, 'offset' | 'limit'>) {
  const { get, viewKey, storeId } = useViewContextAwareHttpClient()
  const mapProduct = useProductMapper()
  return useInfiniteQuery<SearchResult, ProblemDetailsError>({
    queryKey: [...productKeys.search(storeId, viewKey, filter), 'infinite'],
    queryFn: async ({ pageParam = 0 }) => {
      const searchParams = new MyUrlSearchParams({ ...filter, offset: pageParam, limit: 25 })
      const response = await get<ProductSearchResponse>('/products' + searchParams.toString())
      return {
        items: response.items.map(mapProduct),
        pagination: response.pagination,
      }
    },
    getNextPageParam: ({ pagination }) => getNextPageParam(pagination),
    initialPageParam: 0,
    staleTime: ms('1h'),
    enabled: isAnyValueSet(filter),
  })
}

export function useProductMapper() {
  const stockLevelMapper = useStockLevelMapper()
  return (product: ProductDTO) => new ProductWrapper(product, stockLevelMapper.map('UNKNOWN'))
}

/**
 * Seeds the product search cache for a specific GTIN.
 *
 * This function maps the given product to a `ProductWrapper`, constructs the appropriate
 * query key for searching by GTIN, and sets the query data in the cache so that future
 * queries for this GTIN return the provided product immediately.
 *
 * For example, when a user searches for a product by term and selects it,
 * they are redirected to the store map, which performs a search by GTIN.
 * To prevent an extra network request, we pre-populate the cache with the product data for the GTIN search.
 */
function seedProductByGtinCache(client: QueryClient, storeId: string, scanId: string, product: ProductWrapper) {
  const dummyFn = () => Promise.resolve({ items: [], pagination: { total: 0, limit: 0, offset: 0 } })
  const queryKey = getProductSearchOptions({
    get: dummyFn,
    storeId,
    scanId,
    search: { gtin: product.gtin },
    productMapper: () => product,
  }).queryKey
  client.setQueryData(queryKey, { items: [product], pagination: { total: 1, limit: 1, offset: 0 } })
}